import Axios from "../config/axios-v1-config";

// Cobranza del operador (microservice-billing, controller `billing/collections`).
// Solo staff (admin, supervisor, soporte): el BFF lo gatea con AdminOnly y billing lo
// revalida; un rol sin permiso recibe 403. El operador sale siempre del token: ningún body
// lleva `logisticCompanyId` (billing responde 400 a cualquier propiedad desconocida).
const COLLECTIONS = "/billing/collections";

// Resumen de cobranza de TODO el operador (no depende de los filtros de la lista):
// { asOf, unpaid, companiesWithMultipleUnpaid, overdue, dueSoon7d, aging, avgDaysToPay, topDebtors }.
export const getCollectionsDashboard = () => Axios.get(`${COLLECTIONS}/dashboard`);

// ── Configuración: plazo general y regla de recordatorios automáticos ──
// { settings, delivery:{schedulerEnabled, emailConfigured, whatsappConfigured},
//   legacyUnpaidWithoutDueDate, operatorTimeZone }
export const getCollectionSettings = () => Axios.get(`${COLLECTIONS}/settings`);
// Body PARCIAL: lo ausente conserva su valor y `null` lo borra. `applyToLegacyUnpaid`
// completa una vez el vencimiento de las impagas viejas (la respuesta trae `backfill`).
export const updateCollectionSettings = (body) => Axios.put(`${COLLECTIONS}/settings`, body);
// Mismo body que el PUT, sin guardar nada: efecto de la configuración propuesta.
// { legacyWithoutDueDate, wouldBecomeOverdue, companiesNextRun, nextRunAtLocal, operatorTimeZone }
export const previewCollectionSettings = (body) =>
  Axios.post(`${COLLECTIONS}/settings/preview`, body);

// ── Reglas por comercio (plazo propio, pausa de automáticos, contactos de cobranza) ──
// Solo lista los comercios con alguna excepción: { items, count, limit, offset }.
export const listCommerceRules = (params) =>
  Axios.get(`${COLLECTIONS}/commerce-rules`, { params });
// { companyId, rule, effective, effectiveRecipients }. 404 si el comercio no es del operador.
export const getCommerceRule = (companyId) =>
  Axios.get(`${COLLECTIONS}/commerce-rules/${encodeURIComponent(companyId)}`);
// Body PARCIAL; responde la misma vista que el GET.
export const saveCommerceRule = (companyId, body) =>
  Axios.put(`${COLLECTIONS}/commerce-rules/${encodeURIComponent(companyId)}`, body);

// ── Perfil de cobro de un holding (contacto de facturación, correos, teléfono y plazo) ──
// Lo exige la primera emisión de su nota consolidada. Los recordatorios de holdings son de
// una fase posterior: `remindersPaused` se guarda pero hoy no tiene efecto.
// GET → { holdingId, profile: { contactName|null, contactEmails, contactPhone|null (E.164),
//   paymentTermDays|null, paymentTermChosen, remindersPaused, exists, updatedAt, updatedBy },
//   effective: { paymentTermDays|null, source: 'holding'|'operator'|null },
//   missingFields: ('contactName'|'contactEmails'|'contactPhone'|'paymentTerm')[],
//   consolidatedEnabled } (ver `consolidatedEnabledOf` en cobro/utils/holdings).
export const getHoldingProfile = (holdingId) =>
  Axios.get(`${COLLECTIONS}/holding-profiles/${encodeURIComponent(holdingId)}`);
// Body PARCIAL { contactName?, contactEmails?, contactPhone?, paymentTermDays?, remindersPaused? }.
// Mandar `paymentTermDays` (aunque sea null = hereda el del operador) marca el plazo como
// ELEGIDO. Responde la misma vista que el GET. 400 validación o teléfono inválido ·
// 404 holding_not_found · 409 holding_profile_conflict · 503 holdings_unavailable.
export const saveHoldingProfile = (holdingId, body) =>
  Axios.put(`${COLLECTIONS}/holding-profiles/${encodeURIComponent(holdingId)}`, body);
// Perfiles de TODOS los holdings del operador, con el interruptor "Cobro consolidado":
// { items: [{ holdingId, consolidatedEnabled, consolidatedEnabledAt, ...campos del perfil }] }.
// Un holding sin perfil guardado puede no venir: cuenta como apagado.
export const listHoldingProfiles = () => Axios.get(`${COLLECTIONS}/holding-profiles`);
// Interruptor "Cobro consolidado" del holding (dueño: billing). Body { enabled } → 200 con la
// misma vista que el GET por holding. Encender exige que esté LISTO (tarifa propia vigente,
// contacto con nombre, correo y teléfono, plazo elegido y datos fiscales completos): si no,
// 409 { code: 'holding_not_ready', missing: [códigos] }. Apagar siempre se permite.
export const setHoldingConsolidated = (holdingId, enabled) =>
  Axios.put(`${COLLECTIONS}/holding-profiles/${encodeURIComponent(holdingId)}/consolidated`, {
    enabled,
  });

// ── Recordatorios: programados, historial y "enviar ahora" ──
// Body { noteIds, sendAt:{date:'YYYY-MM-DD', hour}, channels:{email, whatsapp},
//        whatsappIntegrationId?, templateIds?, expectedTemplateVersions?, idempotencyKey? }
// con fecha y hora LOCALES del operador. `templateIds: { email?, whatsapp? }` por canal: id =
// esa plantilla, `null` = la del sistema, ausente = la por defecto del operador. La versión
// queda congelada en el envío. 400 invalid_template_reference {fields} si una no está activa
// o es de otro canal. `expectedTemplateVersions: { email?, whatsapp? }` = la versión que
// mostró la vista previa (`null` = la del sistema): si la que se congelaría es otra, 409
// { code: 'template_changed', channel } sin crear nada.
export const createReminderSchedule = (body) => Axios.post(`${COLLECTIONS}/schedules`, body);
export const listReminderSchedules = (params) =>
  Axios.get(`${COLLECTIONS}/schedules`, { params });
// Cancela lo que todavía no salió: { batchId, cancelled }.
export const cancelReminderSchedule = (batchId) =>
  Axios.delete(`${COLLECTIONS}/schedules/${encodeURIComponent(batchId)}`);
// Filtros opcionales: noteId, companyId, batchId, status, channel, trigger, limit, offset.
// Cada fila trae `messageRef` y `messageAvailable` (true solo si GET messages/:messageRef
// devolvería 200: "Ver mensaje" se ofrece solo entonces).
export const listReminderDispatches = (params) =>
  Axios.get(`${COLLECTIONS}/dispatches`, { params });
// Asíncrono (202 { dispatchIds, replayed }): el envío sale en un ciclo corto del worker.
// Body { channels, whatsappIntegrationId?, templateIds?, expectedTemplateVersions?,
// idempotencyKey } (`templateIds` y `expectedTemplateVersions` como en programar).
// `idempotencyKey` (uuid) se genera al abrir el formulario y se reutiliza en los reintentos.
export const remindCobroNote = (noteId, body) =>
  Axios.post(`${COLLECTIONS}/notes/${encodeURIComponent(noteId)}/remind`, body);
// Sin efectos: los mensajes que saldrían HOY, uno por comercio y canal. Body { noteIds,
// channels, templateIds?, companyId?, limit?, offset?, sendAt? } → { asOf, companies:[{
// companyId, companyName, notes }], items, count, limit, offset, skipped:[{ noteId, reason }] }.
// Con `sendAt` (Programar) las notas que ya esperan en otro programado a ±24 h por el mismo
// canal van en `skipped` con reason 'already_scheduled'. 400 invalid_template_reference
// {fields}; 429 { code: 'preview_rate_limited', retryAfterSeconds } si se piden demasiadas.
export const previewReminderMessages = (body) =>
  Axios.post(`${COLLECTIONS}/reminders/preview`, body);
// Texto guardado de un mensaje del historial (`messageRef` de la fila de dispatches):
// { messageRef, channel, companyId, template, requestedTemplates, subject, html, text,
//   noteIds, renderedAt, contentStatus }. `contentStatus`: 'delivered' = el texto que salió;
// 'uncertain' = el del último intento, sin saberse si se entregó. 404 message_not_found si
// no hay texto.
export const getReminderMessage = (messageRef) =>
  Axios.get(`${COLLECTIONS}/messages/${encodeURIComponent(messageRef)}`);

// ── Plantillas de los recordatorios ──
// Billing arma TODO el texto (variables, recortes, formato de montos): el front solo muestra
// lo que devuelve. Errores con `code`: 400 template_invalid {errors, warnings}, 404
// template_not_found, 409 template_name_taken | template_limit_reached {max} |
// template_version_conflict {currentVersion} | template_in_use {usedIn} | idempotency_key_reused.

// { items (activas, por canal y nombre), system: { email, whatsapp }, variables, limits }.
export const listReminderTemplates = () => Axios.get(`${COLLECTIONS}/templates`);
// Body { channel, name, subject? (solo correo), body, idempotencyKey? } → 201 plantilla + `replayed`.
export const createReminderTemplate = (body) => Axios.post(`${COLLECTIONS}/templates`, body);
// Body { name, subject?, body, expectedVersion } (el canal no cambia) → versión nueva.
export const updateReminderTemplate = (templateId, body) =>
  Axios.put(`${COLLECTIONS}/templates/${encodeURIComponent(templateId)}`, body);
// Sin body. 409 template_in_use si la configuración la usa (por defecto o en un paso).
export const archiveReminderTemplate = (templateId) =>
  Axios.post(`${COLLECTIONS}/templates/${encodeURIComponent(templateId)}/archive`);
// Body { channel, idempotencyKey? } → 201 copia editable de la plantilla del sistema.
export const duplicateSystemReminderTemplate = (body) =>
  Axios.post(`${COLLECTIONS}/templates/duplicate-system`, body);
// Sin efectos. Body { channel, subject?, body, source: 'sample'|'company', companyId? } →
// { subject?, html? | text?, length, max, listedNotes, totalNotes, errors, warnings, variants? }.
// 404 company_not_found; 429 { code: 'preview_rate_limited', retryAfterSeconds } si se piden
// demasiadas seguidas; 400 sin `code` si el pedido pasa los topes duros de billing.
export const previewReminderTemplate = (body) =>
  Axios.post(`${COLLECTIONS}/templates/preview`, body);
